import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { getPortalSupabase } from "@/lib/portalSupabase";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, ChevronRight, Loader2 } from "lucide-react";

type PortalAccount = { account_id: string; account_name: string };

export default function PortalAccountPicker() {
  const [, setLocation] = useLocation();
  const [accounts, setAccounts] = useState<PortalAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const supabase = await getPortalSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setLocation("/portal/login");
          return;
        }
        const res = await fetch("/api/portal/accounts", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) throw new Error("Failed to load accounts");
        const data: PortalAccount[] = await res.json();
        if (data.length === 0) {
          setLocation("/portal/login");
          return;
        }
        if (data.length === 1) {
          // Only one account — skip the picker
          localStorage.setItem("portal_account_id", data[0].account_id);
          setLocation("/portal");
          return;
        }
        setAccounts(data);
      } catch (err: any) {
        setError(err.message || "Failed to load accounts");
      } finally {
        setLoading(false);
      }
    })();
  }, [setLocation]);

  function handleSelect(account: PortalAccount) {
    localStorage.setItem("portal_account_id", account.account_id);
    localStorage.setItem("portal_multi_account", "true");
    const returnTo = localStorage.getItem("portal_return_to") || "/portal";
    localStorage.removeItem("portal_return_to");
    setLocation(returnTo);
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building2 className="w-6 h-6 text-primary" />
            <span className="font-semibold text-lg">Carpenter &amp; Company, CPAs</span>
          </div>
          <p className="text-muted-foreground text-sm">Client Portal</p>
        </div>

        <div className="space-y-3">
          <p className="text-center text-sm font-medium">Select an account to continue</p>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading…</span>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          {!loading && accounts.map((account) => (
            <Card
              key={account.account_id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => handleSelect(account)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(account); } }}
            >
              <CardContent className="flex items-center justify-between px-4 py-4">
                <div className="flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="font-medium text-sm">{account.account_name}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
